/**
 * TestRecordNotice.tsx — how a TEST order announces itself to the pharmacy.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane.
 *
 * A test order is only ever on screen because the pharmacist deliberately
 * switched "Show test orders" on, and probably because a disregard email told
 * them to come looking. So these two pieces have one job: make it impossible to
 * mistake the thing they found for work.
 *
 * ⚠️ NEVER COLOUR ALONE (WCAG 2.1 AA, 1.4.1 Use of Colour). Both pieces carry
 * the word "TEST" in text. The amber is a second signal, not the signal — a
 * pharmacist with a colour vision deficiency, on a washed-out dispensary
 * monitor, or reading a print-out gets exactly the same information. This is the
 * kind of surface where that stops being a checklist item: the failure mode of
 * "it looked like a normal row" is a real medicine going to a fake address.
 *
 * WHY THE REASON IS SHOWN VERBATIM. The pharmacist is deciding whether an order
 * in their queue is genuinely a rehearsal, and the clinic's own words are the
 * evidence. A paraphrase would be us asserting something the clinic did not say.
 * It is rendered as plain text into a text node — React escapes it, so there is
 * no markup path out of it — and wrapped so a long note cannot break the layout.
 */
import type { CSSProperties } from 'react';
import type { PharmacyTestRecord } from '../api/types';
import { Alert, Badge } from '../ui/kit';

/** Shown when the mark carried no note — see PharmacyTestRecord.reason. */
export const NO_TEST_REASON = 'No reason was recorded.';

/**
 * The row-level marker.
 *
 * Uses the estate's `Badge` (uppercase, pill) so it reads as the same KIND of
 * thing as the order-state badge beside it, in the `pending` amber tone — the
 * one this console already uses for "look at this before you act".
 */
export function TestRecordBadge() {
  return <Badge tone="pending">Test order</Badge>;
}

/**
 * The detail-panel banner: what it is, why, and that it cannot be dispensed.
 *
 * `role="status"` rather than `alert`: it is the standing condition of a panel
 * the pharmacist chose to open, not an interruption. `alert` would preempt a
 * screen-reader user mid-sentence every time the drawer re-read.
 */
export function TestRecordBanner({ testRecord }: { testRecord: PharmacyTestRecord }) {
  return (
    <Alert tone="warning" role="status" style={{ marginBottom: 0 }}>
      <strong>Test order &mdash; do not dispense.</strong> The clinic marked this as a rehearsal, not a real
      patient. It stays in your queue for reference and the dispensing actions are switched off.
      <span style={reasonBlockStyle}>
        <span style={reasonLabelStyle}>Clinic&rsquo;s reason</span>
        {/* The reason is the clinic's, so it is quoted rather than merged into
            our sentence — the pharmacist can see where our words stop. */}
        <span style={reasonTextStyle}>{testRecord.reason ?? NO_TEST_REASON}</span>
      </span>
    </Alert>
  );
}

const reasonBlockStyle: CSSProperties = {
  display: 'block',
  marginTop: 'var(--gps-s-2)',
  paddingLeft: 'var(--gps-s-3)',
  borderLeft: '3px solid currentColor',
};

const reasonLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  opacity: 0.8,
};

const reasonTextStyle: CSSProperties = {
  display: 'block',
  marginTop: '2px',
  // A note typed into an admin textarea can be long, contain newlines, or be one
  // unbroken string; none of those may push the drawer sideways.
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
};
